/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';
import SEOLocaleList from '@apps/components/SEOLocaleList';
import SideAds from '@apps/components/SideAds';
import { BASE_URL } from '@apps/config';
import { getPostDetails } from '@apps/config/API/details.config';
import getJpjRunningNumber from '@apps/config/API/jpj-running-number.config';
import getNewsListing from '@apps/config/API/listing.config';
import { getNewCarById } from '@apps/config/API/new-car.config';
import NewsListingScreen from '@apps/screens/NewsListing';
import { localeConversions } from '@apps/utils/locale-conversion';
import { Stack } from '@mui/material';
import CheryIcon from '@stack/assets/icons/chery.svg';
import { Breadcrumb } from '@stack/ui/components';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';
import { NextPage, NextPageContext } from 'next';
import Error from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';

import { NewsDetailScreen } from '../../screens/NewsDetail/index';

/**
 * ===========================
 * MAIN
 * ===========================
 */
const Page: NextPage = (props) => {
  // ======================= HOOKS
  const { settings, listing, detail, jpjNumber, webView } = props;
  const router = useRouter();
  const { lang, t } = useTranslation('common');
  const newLang = localeConversions(lang);

  // ======================= STATE
  const carBrand = settings?.car_brand_featured;
  const fuel = settings.fuel_category;
  const pageSize = 40;
  const [data, setData] = useState<any>();
  const [loading, setloading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [cars, setCars] = useState();

  // ======================= API
  const mainCategory = router.query?.category ?? '';
  const subCategory = router.query?.slug?.[0];

  const fetchData = async (pageNumber: number) => {
    try {
      const category = router.query?.category ?? '';
      const subCat = router.query?.slug?.[0];

      setloading(true);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth' as any,
        });
      }, 0);

      const res = await getNewsListing({
        pageSize,
        page: pageNumber,
        locale: newLang,
        postCategory: category as string,
        ...(subCat && category !== subCat ? { subCategory: subCat } : {}),
        isPinned: true,
      });
      if (res.data.length < 1) {
        // router.replace(webView ? '/webview/404' : '/404');
        return <Error statusCode={404} />;
      }

      const newEvCar = await getNewCarById({ id: 4 });
      const newCars = newEvCar?.data?.find((car: any) => {
        return car.new_car_feature_id === 4;
      });

      setCars(newCars);
      setData(res);
      setloading(false);
    } catch (err) {
      console.log('err', err);
    }
  };

  useEffect(() => {
    const { page } = router.query;
    if (!isDetail) {
      setCurrentPage(parseInt(page as string) || 1);
      fetchData(parseInt(page as string) || 1);
    }
  }, [router?.asPath, router?.locale]);

  const news = data;

  // ======================= VARIABLES
  const currentPath = router?.query?.category;
  const currentCategory = settings?.post_category?.find((pc: any) => {
    return pc.post_category_slug === currentPath;
  });
  const isDetail = router?.query?.slug?.[1];
  const subCategoryName = currentCategory?.post_category_item?.find(
    (pci: any) => {
      return pci.post_category_item_slug === subCategory;
    }
  )?.post_category_item_name[newLang];

  let breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: t('news'),
      url: webView ? '/webview' : '/',
    },
    {
      title:
        currentCategory?.post_category_name[newLang] ?? (currentPath as string),
    },
  ];

  if (subCategory && mainCategory !== subCategory) {
    breadcrumbOpt = [
      {
        title: t('news'),
        url: webView ? '/webview' : '/',
      },
      {
        title:
          currentCategory?.post_category_name[newLang] ??
          (currentPath as string),
        url: webView ? `/webview/${currentPath}` : `/${currentPath}`,
      },
      {
        title: subCategoryName,
      },
    ];
  }
  if (isDetail) {
    const categoryPath = detail?.data?.category_name[newLang] ?? '';
    const categorySlug = detail?.data?.category_slug ?? '';
    const subCategoryPath = detail?.data?.category_item_name[newLang] ?? '';
    const subCategorySlug = detail?.data?.category_item_slug;

    breadcrumbOpt = [
      {
        title: t('news'),
        url: webView ? '/webview' : '/',
      },
      {
        title: categoryPath,
        url: webView ? `/webview/${categorySlug}` : `/${categorySlug}`,
      },
    ];

    if (subCategorySlug) {
      breadcrumbOpt.push({
        title: subCategoryPath,
        url: webView
          ? `/webview/${categorySlug}/${subCategoryPath}`
          : `/${categorySlug}/${subCategorySlug}`,
      });
    }
    breadcrumbOpt.push({
      title:
        detail?.data?.post_title?.[newLang] || detail?.data?.post_title['en'],
    });
  }

  // ======================= VIEWS

  // ======================= SEO
  // const seoTitle = isDetail ? detail?.data?.post_title[newLang] : `Malaysia ${currentCategory?.post_category_name[newLang]} News - Latest Models & Upcoming Launches | Caricarz`;
  // const seoDesc = detail?.data?.post_short_description[newLang];
  const seoTitle =
    (currentCategory?.post_category_meta?.title ??
      `Malaysia ${currentCategory?.post_category_name[newLang]} News - Latest Models & Upcoming Launches`) +
    ' | Carz Automedia Malaysia';
  const seoDesc =
    currentCategory?.post_category_meta?.description ??
    detail?.data?.post_short_description[newLang];
  const seoImage = detail?.data?.post_image[newLang];
  const categoryName =
    currentCategory?.post_category_name['en'] === 'Tech' ? (
      <Stack direction="row" gap={1} alignItems={'center'}>
        {t('techNews')} <CheryIcon />
      </Stack>
    ) : (
      currentCategory?.post_category_name[newLang]
    );
  const currentUrl =
    lang === 'en'
      ? `${BASE_URL}${router?.asPath}`
      : `${BASE_URL}/${lang}${router?.asPath}`;

  const cleanedPath = currentUrl.split('?')[0].toLowerCase();

  return (
    <>
      <Head>
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        {seoImage ? (
          <>
            <meta name="twitter:image" content={seoImage} />
            <meta name="twitter:image:type" content="image/jpeg" />
            <meta name="twitter:url" content={cleanedPath} />
          </>
        ) : (
          <meta name="twitter:url" content={cleanedPath} />
        )}
      </Head>
      <NextSeo
        title={seoTitle}
        titleTemplate={seoTitle}
        description={seoDesc}
        openGraph={{
          ...(seoImage
            ? {
                url: cleanedPath,
                images: [
                  {
                    url: seoImage,
                    alt: seoTitle,
                    type: 'image/jpeg',
                  },
                ],
              }
            : { url: currentUrl }),
        }}
        canonical={cleanedPath}
      />
      <SEOLocaleList />
      <SideAds
        adsKeyLeft={'news_listing_vertical_left'}
        adsKeyRight={'news_listing_vertical_right'}
      />
      <div>
        <Breadcrumb options={breadcrumbOpt} />
        {isDetail ? (
          <NewsDetailScreen
            relatedNews={detail?.related_post}
            data={detail?.data}
            listing={listing?.data}
            brands={carBrand}
            fuels={fuel}
            jpjNumber={jpjNumber}
            webView={webView}
          />
        ) : (
          <>
            <h1 className="text-3xl">
              {subCategory ? subCategoryName : categoryName}
            </h1>
            <NewsListingScreen
              categorySlug={currentPath}
              data={news?.data}
              currentPage={currentPage}
              totalCount={news?.meta?.total ?? 0}
              pageSize={pageSize}
              carBrand={carBrand}
              newEvCar={cars}
              webView={webView}
              loading={loading}
              href={`/${currentPath}`}
            />
          </>
        )}
      </div>
    </>
  );
};

export async function getServerSideProps(ctx: NextPageContext) {
  const hasDetailSlug = ctx.query?.slug && ctx.query?.slug?.length > 1;
  let locale = ctx?.locale ?? 'en';
  const newLocale = localeConversions(locale);
  if (hasDetailSlug) {
    const titleSlug = ctx.query?.slug?.[1] as string;
    const id = ctx.query?.slug?.[2] as string;
    const detailsData = await getPostDetails({
      slug: titleSlug,
      id,
      locale: newLocale,
    });

    // Check is the post have the available language
    const availableLang = detailsData?.data?.post_available_language as string;
    const hasData = availableLang?.includes(newLocale);
    if (!hasData) {
      // return { notFound: true };
      locale = 'en';
    }

    const listingData = await getNewsListing({
      pageSize: 8,
      page: 1,
      locale: newLocale,
      postCategory: detailsData?.data?.category_slug ?? '',
      isPinned: true,
    });

    const jpjNumber = await getJpjRunningNumber({ featured: true });

    return {
      props: {
        listing: listingData,
        detail: detailsData,
        jpjNumber,
      },
    };
  }

  const category = ctx.query?.category ?? '';
  const data = await getNewsListing({
    pageSize: 40,
    page: 1,
    locale: newLocale,
    postCategory: category as string,
    isPinned: true,
  });
  const hasData = data?.data?.length > 0 ? true : false;
  if (!hasData) {
    return { notFound: true };
  }

  return { props: {} };
}

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default Page;
